import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useReducer,
  useLayoutEffect,
} from 'react';
import { T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import {
  Funnel,
  SlidersHorizontal,
  X,
  SquaresFour,
  SpinnerGap,
} from '@phosphor-icons/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TablePagination, PaginationState } from './TablePagination';
import GentlyTable, { TableColumn, SummaryRow } from './GentlyTable';
import { togglePageSelection } from './pageSelection';

// Re-export types from GentlyTable for backward compatibility
export type { SortDirection, TableColumn, SummaryRow } from './GentlyTable';

const EMPTY_SEARCH_FIELDS: never[] = [];

export interface PaginationConfig {
  enabled: boolean;
  pagination: PaginationState;
  onPageSizeChange: (pageSize: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  onGoToPage?: (page: number) => void;
  showRowsPerPage?: boolean;
  showPageInfo?: boolean;
  showPageNumbers?: boolean;
  rowsPerPageOptions?: number[];
}

export interface PaginatedGentlyTableProps<T> {
  data: T[] | undefined;
  columns: TableColumn<T>[];
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  searchFields?: (keyof T)[];
  showSearch?: boolean;
  /** Rendered between the table header row and the table. See `GentlyTableProps.belowHeaderSlot`. */
  belowHeaderSlot?: React.ReactNode;
  /** Custom node rendered in place of the default search input. */
  searchSlot?: React.ReactNode;
  actions?: React.ReactNode;
  summaryRow?: SummaryRow;
  isLoading?: boolean;
  className?: string;
  showDownload?: boolean;
  downloadFilename?: string;
  showCount?: boolean; // Show current count of displayed items
  itemName?: string; // Name of items being displayed (e.g., "companies", "orders")
  customCountDisplay?: React.ReactNode; // Custom count display component

  // Scrollable table options
  maxHeight?: number | string; // Max height for table body (e.g., 600 or "600px")
  stickyHeader?: boolean; // Whether to make the header sticky when scrolling
  /** Size columns to their content instead of stretching. See `GentlyTableProps.sizeToContent`. */
  sizeToContent?: boolean;

  // Pagination props
  paginationConfig?: PaginationConfig;

  // Server-side features
  serverSideSearch?: {
    value: string;
    onChange: (value: string) => void;
    debounceMs?: number;
    /**
     * Renders a VS Code-style `.*` toggle inside the search box for switching
     * between substring and regex matching. Omit to hide the toggle entirely.
     */
    regexToggle?: {
      active: boolean;
      onToggle: () => void;
      /** Flags an invalid pattern (red border) — server rejects it too. */
      invalid?: boolean;
      /** Explanation shown as a tooltip on the search box when invalid. */
      invalidMessage?: string;
    };
  };
  serverSideSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  };
  defaultSort?: {
    sortBy: keyof T;
    sortDirection: 'asc' | 'desc';
  };
  getRowKey?: (row: T, index: number) => string;
  getRowClassName?: (row: T) => string;
  renderExpandedRow?: (row: T) => React.ReactNode;
  /**
   * Keep each expanded panel mounted after first open and toggle visibility
   * instead of unmounting on collapse. See `GentlyTableProps.persistExpandedContent`.
   */
  persistExpandedContent?: boolean;
  /**
   * Seeds a row's expanded state when it mounts (deep-link auto-expand).
   * See `GentlyTableProps.getInitialExpanded`.
   */
  getInitialExpanded?: (row: T) => boolean;
  /** Called after a row's expansion is toggled by clicking it. */
  onExpandedRowChange?: (row: T, expanded: boolean) => void;
  /**
   * Opt-in drag-and-drop column header reordering. Controlled — the parent
   * reorders its `columns` array on callback. See `GentlyTableProps.enableColumnReorder`.
   */
  enableColumnReorder?: boolean;
  onColumnOrderChange?: (orderedIds: string[]) => void;
  /**
   * Opt-in: render each row as a real link (right-click → "Open in new tab",
   * cmd/middle-click, etc.). See `GentlyTableProps.renderRowLink`.
   */
  renderRowLink?: (row: T, className: string) => React.ReactNode;
  /**
   * Built-in client-side pagination over the full `data` array (filtered +
   * sorted in-component). Use for fully-loaded lists; for server-driven
   * pagination pass `paginationConfig` instead.
   */
  clientPagination?: {
    pageSize?: number;
    rowsPerPageOptions?: number[];
  };

  // Column visibility and filtering (opt-in; set columnPickerEnabled to enable)
  columnPickerEnabled?: boolean;
  defaultVisibleColumnIds?: (keyof T)[];
  visibleColumnIds?: (keyof T)[];
  onVisibleColumnsChange?: (ids: (keyof T)[]) => void;
  filterableColumns?: (keyof T)[];
  distinctValues?: Record<string, string[]>;
  activeFilters?: Record<string, string[]>;
  onFilterChange?: (filters: Record<string, string[]>) => void;
  onFilterOpen?: (columnId: string) => void;

  // Row selection (opt-in; set selectable to enable)
  selectable?: boolean;
  selectedIds?: Set<string>;
  allSelected?: boolean;
  onSelectionChange?: (selectedIds: Set<string>, allSelected: boolean) => void;
  /**
   * What the header checkbox selects. `'all'` (the default) reports
   * `allSelected` with an empty id set, meaning "everything matching the
   * filter". `'page'` adds the rows on screen to `selectedIds` (or removes
   * them) and never reports `allSelected`, so a selection can span pages and
   * always names its rows.
   *
   * `'page'` takes "the page" to be `data`, so it is for server-driven
   * pagination (`paginationConfig`) where `data` is exactly the rows on
   * screen. Don't combine it with `clientPagination` or client-side search
   * (`showSearch` without `serverSideSearch`): `data` then also holds rows
   * that are not displayed, and the header would select those too.
   */
  selectAllScope?: 'all' | 'page';
}

function ColumnPicker<T>({
  allColumns,
  visibleIds,
  defaultIds,
  onChange,
}: {
  allColumns: TableColumn<T>[];
  visibleIds: (keyof T)[];
  defaultIds: (keyof T)[];
  onChange: (ids: (keyof T)[]) => void;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const filtered = allColumns.filter((c) =>
    c.label.toLowerCase().includes(search.toLowerCase()),
  );
  const visibleSet = new Set<keyof T>(visibleIds);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <SquaresFour className="h-4 w-4" />{' '}
          <T context="button label for column visibility picker">Columns</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end">
        <Input
          placeholder={gt('Search columns...', {
            context: 'placeholder for column picker search input',
          })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2 h-8 text-sm"
        />
        <div className="max-h-60 overflow-y-auto space-y-1">
          {filtered.map((col) => {
            const checked = visibleSet.has(col.id);
            return (
              <label
                key={String(col.id)}
                className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    onChange(
                      checked
                        ? visibleIds.filter((id) => id !== col.id)
                        : [...visibleIds, col.id],
                    );
                  }}
                />
                {col.label}
              </label>
            );
          })}
        </div>
        <div className="mt-2 flex gap-2 border-t pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 text-xs"
            onClick={() => onChange(allColumns.map((c) => c.id))}
          >
            <T context="button to show all table columns">Show All</T>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 text-xs"
            onClick={() => onChange(defaultIds)}
          >
            <T context="button to reset table columns to default selection">
              Reset
            </T>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ColumnFilterPopover({
  columnId,
  columnLabel,
  values,
  selected,
  onToggle,
  onOpen,
  isLoading,
}: {
  columnId: string;
  columnLabel: string;
  values: string[] | undefined;
  selected: Set<string>;
  onToggle: (columnId: string, value: string) => void;
  onOpen: () => void;
  isLoading: boolean;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const filtered = (values ?? []).filter((v) =>
    v.toLowerCase().includes(search.toLowerCase()),
  );
  const hasActive = selected.size > 0;

  return (
    <Popover onOpenChange={(open) => open && onOpen()}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-6 w-6 p-0', hasActive && 'text-primary')}
          title={gt('Filter {column}', {
            context: 'tooltip for column filter button',
            column: columnLabel,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <Funnel
            className="h-3.5 w-3.5"
            weight={hasActive ? 'fill' : 'regular'}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          <T context="filter popover header showing column name to filter by">
            Filter by <Var>{columnLabel}</Var>
          </T>
        </p>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 justify-center">
            <SpinnerGap className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              <T context="loading indicator while filter values are being fetched">
                Loading...
              </T>
            </span>
          </div>
        ) : !values || values.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">
            <T context="empty state in filter popover when column has no distinct values">
              No distinct values
            </T>
          </p>
        ) : (
          <>
            {values.length > 8 && (
              <Input
                placeholder={gt('Search...', {
                  context:
                    'placeholder for filtering distinct values in column filter popover',
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2 h-7 text-xs"
              />
            )}
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filtered.map((val) => (
                <label
                  key={val}
                  className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(val)}
                    onCheckedChange={() => onToggle(columnId, val)}
                  />
                  <span className="truncate">{val}</span>
                </label>
              ))}
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground py-1">
                  <T context="empty state when search finds no matching filter values">
                    No matches
                  </T>
                </p>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FilterChips<T>({
  activeFilters,
  columns,
  onRemove,
  onClearAll,
}: {
  activeFilters: Record<string, string[]>;
  columns: TableColumn<T>[];
  onRemove: (columnId: string, value: string) => void;
  onClearAll: () => void;
}) {
  const entries = Object.entries(activeFilters).filter(([, v]) => v.length > 0);
  if (entries.length === 0) return null;
  const colLabel = (id: string) =>
    columns.find((c) => String(c.id) === id)?.label ?? id;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
      {entries.flatMap(([colId, vals]) =>
        vals.map((val) => (
          <Badge
            key={`${colId}:${val}`}
            variant="secondary"
            className="gap-1 text-xs font-normal cursor-pointer"
            onClick={() => onRemove(colId, val)}
          >
            {colLabel(colId)}: {val}
            <X className="h-3 w-3" />
          </Badge>
        )),
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-5 px-1.5 text-xs text-muted-foreground"
        onClick={onClearAll}
      >
        <T context="button to clear all active table filters">Clear all</T>
      </Button>
    </div>
  );
}

type InternalSelectionState = {
  selectedIds: Set<string>;
  allSelected: boolean;
};
type InternalSelectionAction = {
  type: 'set';
  selectedIds: Set<string>;
  allSelected: boolean;
};

function internalSelectionReducer(
  _state: InternalSelectionState,
  action: InternalSelectionAction,
): InternalSelectionState {
  return { selectedIds: action.selectedIds, allSelected: action.allSelected };
}

/**
 * A shadcn-based table component with pagination support.
 * Supports both client-side and server-side search, sorting, and pagination.
 * Set `columnPickerEnabled` to opt into column visibility and filtering features.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PaginatedGentlyTable<T extends Record<string, any>>({
  data,
  columns,
  columnPickerEnabled = false,
  defaultVisibleColumnIds,
  visibleColumnIds: controlledVisibleIds,
  onVisibleColumnsChange,
  filterableColumns,
  distinctValues,
  activeFilters: controlledFilters,
  onFilterChange,
  onFilterOpen,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data found',
  searchFields = EMPTY_SEARCH_FIELDS,
  showSearch = true,
  belowHeaderSlot,
  searchSlot,
  actions,
  summaryRow,
  isLoading = false,
  className,
  showDownload = true,
  downloadFilename = 'table-data',
  showCount = false,
  itemName = 'items',
  customCountDisplay,
  maxHeight,
  stickyHeader,
  sizeToContent,
  paginationConfig,
  serverSideSearch,
  serverSideSorting,
  defaultSort,
  getRowKey,
  getRowClassName,
  renderExpandedRow,
  persistExpandedContent,
  getInitialExpanded,
  onExpandedRowChange,
  renderRowLink,
  clientPagination,
  enableColumnReorder,
  onColumnOrderChange,
  selectable = false,
  selectedIds: controlledSelectedIds,
  allSelected: controlledAllSelected,
  onSelectionChange,
  selectAllScope = 'all',
}: PaginatedGentlyTableProps<T>) {
  const allIds = useMemo(() => columns.map((c) => c.id), [columns]);
  const defaultIds = defaultVisibleColumnIds ?? allIds;

  // Row selection state — internal fallback for uncontrolled mode
  const [internalSelection, dispatchInternalSelection] = useReducer(
    internalSelectionReducer,
    { selectedIds: new Set<string>(), allSelected: false },
  );
  const internalSelectedIds = internalSelection.selectedIds;
  const internalAllSelected = internalSelection.allSelected;
  // Controlled props take precedence over internal state
  const selectedIds = controlledSelectedIds ?? internalSelectedIds;
  const allSelected = controlledAllSelected ?? internalAllSelected;
  // Stable refs so event handler closures don't go stale between renders
  const selectedIdsRef = useRef(selectedIds);
  const allSelectedRef = useRef(allSelected);
  const dataRef = useRef(data);

  useLayoutEffect(() => {
    selectedIdsRef.current = selectedIds;
    allSelectedRef.current = allSelected;
    dataRef.current = data;
  });

  // null = user hasn't customized yet; derive from defaultIds in that case
  const [userVisibleIds, setUserVisibleIds] = useState<(keyof T)[] | null>(
    null,
  );

  const visibleIds = controlledVisibleIds ?? userVisibleIds ?? defaultIds;
  const setVisibleIds = useCallback(
    (ids: (keyof T)[]) => {
      if (onVisibleColumnsChange) onVisibleColumnsChange(ids);
      else setUserVisibleIds(ids);
    },
    [onVisibleColumnsChange],
  );

  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds]);
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleSet.has(c.id)),
    [columns, visibleSet],
  );

  const [internalFilters, setInternalFilters] = useState<
    Record<string, string[]>
  >({});
  const activeFilters = controlledFilters ?? internalFilters;
  const setFilters = useCallback(
    (f: Record<string, string[]>) => {
      if (onFilterChange) onFilterChange(f);
      else setInternalFilters(f);
    },
    [onFilterChange],
  );

  const toggleFilter = useCallback(
    (columnId: string, value: string) => {
      const current = activeFilters[columnId] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setFilters({ ...activeFilters, [columnId]: next });
    },
    [activeFilters, setFilters],
  );

  const removeFilter = useCallback(
    (columnId: string, value: string) => {
      const current = activeFilters[columnId] ?? [];
      setFilters({
        ...activeFilters,
        [columnId]: current.filter((v) => v !== value),
      });
    },
    [activeFilters, setFilters],
  );

  const clearAllFilters = useCallback(() => setFilters({}), [setFilters]);

  const [loadingFilterCol, setLoadingFilterCol] = useState<string | null>(null);

  const handleFilterOpen = useCallback(
    (columnId: string) => {
      if (onFilterOpen) {
        const vals = distinctValues?.[columnId];
        if (!vals || vals.length === 0) {
          setLoadingFilterCol(columnId);
        }
        onFilterOpen(columnId);
      }
    },
    [onFilterOpen, distinctValues],
  );

  const effectiveColumns = columnPickerEnabled ? visibleColumns : columns;

  const getRowId = useCallback(
    (row: T, index: number) =>
      getRowKey ? getRowKey(row, index) : String(index),
    [getRowKey],
  );

  const handleHeaderCheckboxChange = useCallback(
    (checked: boolean | 'indeterminate') => {
      if (selectAllScope === 'page') {
        const pageIds = (dataRef.current ?? []).map((r, i) => getRowId(r, i));
        const nextIds = togglePageSelection(
          selectedIdsRef.current,
          pageIds,
          checked === true,
        );
        dispatchInternalSelection({
          type: 'set',
          selectedIds: nextIds,
          allSelected: false,
        });
        onSelectionChange?.(nextIds, false);
        return;
      }
      if (checked) {
        dispatchInternalSelection({
          type: 'set',
          selectedIds: new Set(),
          allSelected: true,
        });
        onSelectionChange?.(new Set(), true);
      } else {
        dispatchInternalSelection({
          type: 'set',
          selectedIds: new Set(),
          allSelected: false,
        });
        onSelectionChange?.(new Set(), false);
      }
    },
    [selectAllScope, getRowId, onSelectionChange],
  );

  const handleRowCheckboxChange = useCallback(
    (rowId: string, checked: boolean | 'indeterminate') => {
      let nextIds: Set<string>;
      let nextAllSelected: boolean;
      if (allSelectedRef.current) {
        // Turn off allSelected; select all visible rows except this one if unchecking
        const currentPageIds = new Set(
          (dataRef.current ?? []).map((r, i) => getRowId(r, i)),
        );
        if (!checked) currentPageIds.delete(rowId);
        nextIds = currentPageIds;
        nextAllSelected = false;
      } else {
        nextIds = new Set(selectedIdsRef.current);
        if (checked) nextIds.add(rowId);
        else nextIds.delete(rowId);
        nextAllSelected = false;
      }
      dispatchInternalSelection({
        type: 'set',
        selectedIds: nextIds,
        allSelected: nextAllSelected,
      });
      onSelectionChange?.(nextIds, nextAllSelected);
    },
    [getRowId, onSelectionChange],
  );

  const checkboxColumn: TableColumn<T> = useMemo(() => {
    const currentPageIds = (data ?? []).map((row, i) => getRowId(row, i));
    const allCurrentPageSelected =
      allSelected ||
      (currentPageIds.length > 0 &&
        currentPageIds.every((id) => selectedIds.has(id)));
    const someCurrentPageSelected =
      allSelected || currentPageIds.some((id) => selectedIds.has(id));
    const headerChecked = allCurrentPageSelected
      ? true
      : someCurrentPageSelected
        ? 'indeterminate'
        : false;

    return {
      id: '__select__' as keyof T,
      label: '',
      stopRowClick: true,
      className: 'w-10',
      headerRender: () => (
        <Checkbox
          checked={headerChecked}
          onCheckedChange={handleHeaderCheckboxChange}
        />
      ),
      render: (_value: unknown, row: T) => {
        const rowId = getRowId(row, 0);
        const isSelected = allSelected || selectedIds.has(rowId);
        return (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) =>
              handleRowCheckboxChange(rowId, checked)
            }
          />
        );
      },
    };
  }, [
    data,
    getRowId,
    allSelected,
    selectedIds,
    handleHeaderCheckboxChange,
    handleRowCheckboxChange,
  ]);

  const columnsWithFilters = useMemo(() => {
    if (
      !columnPickerEnabled ||
      !filterableColumns ||
      filterableColumns.length === 0
    )
      return effectiveColumns;
    const filterableSet = new Set(filterableColumns.map(String));
    return effectiveColumns.map((col) => {
      if (!filterableSet.has(String(col.id))) return col;
      const colId = String(col.id);
      const vals = distinctValues?.[colId];
      const selected = new Set(activeFilters[colId] ?? []);
      return {
        ...col,
        headerRender: () => (
          <div className="flex items-center gap-1">
            <span className="font-medium text-sm">{col.label}</span>
            <ColumnFilterPopover
              columnId={colId}
              columnLabel={col.label}
              values={vals}
              selected={selected}
              onToggle={toggleFilter}
              onOpen={() => handleFilterOpen(colId)}
              isLoading={
                loadingFilterCol === colId &&
                !(distinctValues && loadingFilterCol in distinctValues)
              }
            />
          </div>
        ),
      };
    });
  }, [
    columnPickerEnabled,
    effectiveColumns,
    filterableColumns,
    distinctValues,
    activeFilters,
    toggleFilter,
    handleFilterOpen,
    loadingFilterCol,
  ]);

  const finalColumns = useMemo(
    () =>
      selectable ? [checkboxColumn, ...columnsWithFilters] : columnsWithFilters,
    [selectable, checkboxColumn, columnsWithFilters],
  );

  const actionBar = columnPickerEnabled ? (
    <div className="flex items-center gap-2">
      {actions}
      <ColumnPicker
        allColumns={columns}
        visibleIds={visibleIds}
        defaultIds={defaultIds}
        onChange={setVisibleIds}
      />
    </div>
  ) : (
    actions
  );

  // Determine if client-side sorting should be disabled
  // Disable when pagination is enabled or server-side sorting is used
  const disableClientSideSort =
    paginationConfig?.enabled || !!serverSideSorting;

  return (
    <div className={cn('space-y-4', className)}>
      {columnPickerEnabled && (
        <FilterChips
          activeFilters={activeFilters}
          columns={columns}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}
      <div>
        <GentlyTable
          data={data}
          columns={finalColumns}
          searchPlaceholder={searchPlaceholder}
          onRowClick={onRowClick}
          emptyMessage={emptyMessage}
          searchFields={searchFields}
          showSearch={showSearch}
          searchSlot={searchSlot}
          belowHeaderSlot={belowHeaderSlot}
          actions={actionBar}
          summaryRow={summaryRow}
          isLoading={isLoading}
          showDownload={showDownload}
          downloadFilename={downloadFilename}
          showCount={showCount}
          maxHeight={maxHeight}
          stickyHeader={stickyHeader}
          sizeToContent={sizeToContent}
          customCountDisplay={
            customCountDisplay !== undefined ? (
              customCountDisplay
            ) : showCount &&
              paginationConfig?.pagination.totalItems !== undefined ? (
              <div className="text-sm text-muted-foreground">
                {paginationConfig.pagination.totalItems === 0
                  ? `0 ${itemName}`
                  : paginationConfig.pagination.startItem !== undefined &&
                      paginationConfig.pagination.endItem !== undefined
                    ? `${paginationConfig.pagination.startItem}–${paginationConfig.pagination.endItem} of ${paginationConfig.pagination.totalItems} ${itemName}`
                    : `${paginationConfig.pagination.totalItems} ${itemName}`}
              </div>
            ) : undefined
          }
          serverSideSearch={serverSideSearch}
          serverSideSorting={serverSideSorting}
          disableClientSideSort={disableClientSideSort}
          downloadAllData={data} // For paginated tables, download all data
          defaultSort={defaultSort}
          getRowKey={getRowKey}
          getRowClassName={getRowClassName}
          renderExpandedRow={renderExpandedRow}
          persistExpandedContent={persistExpandedContent}
          getInitialExpanded={getInitialExpanded}
          onExpandedRowChange={onExpandedRowChange}
          renderRowLink={renderRowLink}
          clientPagination={clientPagination}
          enableColumnReorder={enableColumnReorder}
          onColumnOrderChange={onColumnOrderChange}
        />
        {paginationConfig?.enabled && <TablePagination {...paginationConfig} />}
      </div>
    </div>
  );
}

export default PaginatedGentlyTable;
